import { session } from 'feature/widget/SessionStore'
import { try_on } from 'feature/widget/TryOnStore'
import { ym } from 'feature/yama'
import { useLatestCallback } from 'hook/useLatestCallback'
import { employeeModal, EmployeeModalStore } from 'page/catalog/employee/EmployeeModal/EmployeeModalStore'
import { employee } from 'page/catalog/employee/store/EmployeeStore'
import { useDemo } from 'page/catalog/store/CatalogContext'
import { comboCartStore } from 'page/catalog/store/ComboCartStore'
import { user } from 'page/catalog/store/UserStore'
import { userModal, UserModalStore } from 'page/catalog/UserModal/store/UserModalStore'
import { useEffect, useRef, useState } from 'react'

interface UseCombineTryOnOptions {
  topItemId: string | null
  bottomItemId: string | null
  onComplete?: () => void
}

export function useCombineTryOn({ topItemId, bottomItemId, onComplete }: UseCombineTryOnOptions) {
  const { boutique, data } = useDemo()
  const [isGenerating, setIsGenerating] = useState(false)
  const generatingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const windowStateRef = useRef<'none' | 'min' | 'max'>('none')
  const MIN_LOADING_TIME = 3000

  const finishLoading = useLatestCallback(() => {
    setIsGenerating(false)
    if (generatingTimeoutRef.current) {
      clearTimeout(generatingTimeoutRef.current)
      generatingTimeoutRef.current = null
    }
    if (onComplete) {
      onComplete()
    }
    startTimeRef.current = null
  })

  useEffect(() => {
    if (isGenerating) {
      setIsGenerating(false)
      if (generatingTimeoutRef.current) {
        clearTimeout(generatingTimeoutRef.current)
        generatingTimeoutRef.current = null
      }
      startTimeRef.current = null
    }
  }, [topItemId, bottomItemId])

  // Завершение лоадера с учётом минимального времени
  const finishLoadingWithMinTime = useLatestCallback(() => {
    if (startTimeRef.current) {
      const elapsed = Date.now() - startTimeRef.current
      const remaining = MIN_LOADING_TIME - elapsed

      if (remaining > 0) {
        // Ждем оставшееся время до минимального
        generatingTimeoutRef.current = setTimeout(() => {
          finishLoading()
        }, remaining)
      } else {
        // Уже прошло достаточно времени
        finishLoading()
      }
    } else {
      // На всякий случай, если startTime не был установлен
      finishLoading()
    }
  })

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Проверяем, что сообщение от виджета
      if (event.data.source !== 'showoff-try-on') return

      const { name, message } = event.data

      // Обработка события состояния виджета
      if (name === 'on-state') {
        const { window: windowState, screen } = message

        // Сохраняем текущее состояние окна
        windowStateRef.current = windowState

        // Если виджет открыт и экран profile или policy - закрываем комбокорзину
        if (windowState === 'max' && (screen === 'profile' || screen === 'policy')) {
          if (startTimeRef.current) {
            finishLoading()
          }
          comboCartStore.open = false
          return
        }

        // Если лоадер не активен, игнорируем дальнейшую обработку
        if (!startTimeRef.current) return

        return
      }

      // Проверяем, что событие относится к одному из наших товаров
      const isOurItem = message?.item_id === topItemId || message?.item_id === bottomItemId
      if (try_on.currentItemId !== topItemId && try_on.currentItemId !== bottomItemId) return

      if (!isOurItem) return

      switch (name) {
        case 'on-generation-start':
          // Убираем лоадер только если виджет открыт
          if (windowStateRef.current === 'max') {
            finishLoadingWithMinTime()
          }
          break
        case 'on-generation-start-success':
          if (session.started) {
            ym('session_generation_start_success', { session_id: session.session_id, ...message })
          }
          // Убираем лоадер при успешном старте генерации (с учётом минимального времени)
          finishLoadingWithMinTime()
          break
        case 'on-generation-start-error':
          // Убираем лоадер при ошибке генерации СРАЗУ
          finishLoading()
          break
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
      if (generatingTimeoutRef.current) {
        clearTimeout(generatingTimeoutRef.current)
      }
    }
  }, [topItemId, bottomItemId, onComplete, finishLoading, finishLoadingWithMinTime])

  const onCombine = useLatestCallback(async () => {
    if (!topItemId || !bottomItemId) return

    ym('click_try_on', { from: 'combo_cart', topItemId, bottomItemId })

    // Проверка бутика
    if (boutique) {
      if (!employee.user) {
        employeeModal.open(new EmployeeModalStore())
        return
      }
      if (!user.present) {
        userModal.open(new UserModalStore(data))
        return
      }
    }

    setIsGenerating(true)
    startTimeRef.current = Date.now() // Запоминаем время начала

    void try_on.open(topItemId, bottomItemId)

    if (generatingTimeoutRef.current) {
      clearTimeout(generatingTimeoutRef.current)
    }

    // Fallback timeout на случай, если события postMessage не придут
    // Учитываем минимальное время + запас
    generatingTimeoutRef.current = setTimeout(() => {
      finishLoading()
    }, Math.max(MIN_LOADING_TIME, 10000))
  })

  return {
    isGenerating,
    onCombine,
  }
}
